import { useContext, useState } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import { callAPI, unsafeAPI } from '../../hooks/api';
import firestore from '../../firestore';
import Input from '../../components/Input';
import { Button } from '../../shadcn/Button';
import Notice from '../../components/Notice';
import { TabBar } from '../../components/TabBar';
import { ArrowRightLeft, Lock, Search, Smartphone, Tag, Unlock } from 'lucide-react';
import { COUNTRIES, COUNTRY_LABELS } from '../../utils/constants';

type ConsultaResult = {
	status?: string;
	success?: boolean;
	[key: string]: unknown;
};

type ParsedStatusItem = {
	label: string;
	value: string;
	tone: 'success' | 'error' | 'warning' | 'neutral';
};

type ParsedStatusSection = {
	title: string;
	items: ParsedStatusItem[];
	chips: string[];
};

type DeviceLookupData = {
	device_deleted?: boolean;
	is_active?: boolean;
	seller_store?: string;
	device_model?: string;
	product_description?: string;
	user?: { id?: string } | null;
};

type DeviceTarget = {
	id: string;
	imei: string;
	userEmail?: string;
	store?: string;
	model?: string;
};

type ImeiLockAction = '' | 'lock' | 'unlock';

const SECTION_LABELS: Record<string, string> = {
	krece: 'Krece',
	nuovo: 'Nuovo',
	'proveedores mdm': 'Proveedores MDM',
	'proveedores mdm activos': 'Proveedores MDM',
};

const FIELD_LABELS: Record<string, string> = {
	activo: 'Estado',
	usuario: 'Usuario',
	tienda: 'Tienda',
	modelo: 'Modelo',
	país: 'País',
	pais: 'País',
	id: 'ID',
	enrollment_status: 'Estado de registro',
	status: 'Estado',
};

const formatLabel = (label: string) => {
	const normalizedLabel = label.trim();
	const mappedLabel = FIELD_LABELS[normalizedLabel.toLowerCase()];

	if (mappedLabel) {
		return mappedLabel;
	}

	return normalizedLabel
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatValue = (label: string, value: string) => {
	const normalizedValue = value.trim();
	const normalizedLabel = label.trim().toLowerCase();

	if (normalizedLabel === 'activo') {
		return ['true', 'activo', 'active'].includes(normalizedValue.toLowerCase()) ? 'Activo' : 'Inactivo';
	}

	return normalizedValue;
};

const getValueTone = (label: string, value: string): ParsedStatusItem['tone'] => {
	const normalizedLabel = label.trim().toLowerCase();
	const normalizedValue = value.trim().toLowerCase();

	if (normalizedLabel === 'activo') {
		return ['true', 'activo', 'active'].includes(normalizedValue) ? 'success' : 'error';
	}
	if (normalizedValue.includes('no encontrado') || normalizedValue === 'false' || normalizedValue === 'inactivo') {
		return 'warning';
	}
	if (['completed', 'enrolled', 'active', 'activo', 'true'].includes(normalizedValue)) {
		return 'success';
	}
	if (['locked', 'bloqueado', 'failed', 'error'].includes(normalizedValue)) {
		return 'error';
	}

	return 'neutral';
};

const parseStatusItems = (value: string): ParsedStatusItem[] =>
	value
		.split(/,\s*/)
		.map((item) => item.trim())
		.filter(Boolean)
		.map((item) => {
			const separatorIndex = item.indexOf('=');
			if (separatorIndex === -1) {
				return {
					label: 'Estado',
					value: item,
					tone: getValueTone('Estado', item),
				};
			}

			const label = item.slice(0, separatorIndex);
			const rawValue = item.slice(separatorIndex + 1);
			const formattedValue = formatValue(label, rawValue);

			return {
				label: formatLabel(label),
				value: formattedValue,
				tone: getValueTone(label, formattedValue),
			};
		});

const parseConsultaStatus = (status: string): ParsedStatusSection[] =>
	status
		.split(/\s*\|\s*/)
		.map((section) => section.trim())
		.filter(Boolean)
		.map((section) => {
			const separatorIndex = section.indexOf(':');
			if (separatorIndex === -1) {
				return {
					title: 'Estado',
					items: [{ label: 'Estado', value: section, tone: getValueTone('Estado', section) }],
					chips: [],
				};
			}

			const rawTitle = section.slice(0, separatorIndex).trim();
			const value = section.slice(separatorIndex + 1).trim();
			const title = SECTION_LABELS[rawTitle.toLowerCase()] || rawTitle;

			if (rawTitle.toLowerCase().startsWith('proveedores mdm') && !value.toLowerCase().includes('no encontrado')) {
				return {
					title,
					items: [],
					chips: value.split(/,\s*/).filter(Boolean),
				};
			}

			return {
				title,
				items: parseStatusItems(value),
				chips: [],
			};
		});

const toneClasses: Record<ParsedStatusItem['tone'], string> = {
	success: 'bg-green-500/10 text-green-300 ring-green-500/30',
	error: 'bg-red-500/10 text-red-300 ring-red-500/30',
	warning: 'bg-yellow-500/10 text-yellow-300 ring-yellow-500/30',
	neutral: 'bg-neutral-700/70 text-neutral-100 ring-neutral-600/60',
};

const findActiveDeviceByImei = async (imei: string): Promise<DeviceTarget | null> => {
	const snapshot = await firestore.collection('Devices').where('device_imei_no', '==', imei).get();
	const devices = snapshot.docs
		.map((doc) => ({ id: doc.id, data: doc.data() as DeviceLookupData }))
		.filter(({ data }) => !data.device_deleted);
	const activeDevice = devices.find(({ data }) => data.is_active);

	if (!activeDevice) {
		return null;
	}

	return {
		id: activeDevice.id,
		imei,
		userEmail: activeDevice.data.user?.id,
		store: activeDevice.data.seller_store,
		model: activeDevice.data.device_model || activeDevice.data.product_description,
	};
};

export default function GestionImeis() {
	const { userData, internalCountry } = useContext(GlobalContext);
	const [activeTab, setActiveTab] = useState('consulta');

	// --- Liberación de IMEI state ---
	const [releaseImei, setReleaseImei] = useState('');
	const [releaseLoading, setReleaseLoading] = useState(false);
	const [releaseResult, setReleaseResult] = useState<{ message: string; variant: 'success' | 'error' | 'warning' } | null>(
		null,
	);

	// --- Consulta de IMEI state ---
	const [consultaImei, setConsultaImei] = useState('');
	const [consultaLoading, setConsultaLoading] = useState(false);
	const [consultaResult, setConsultaResult] = useState<ConsultaResult | null>(null);
	const [consultaError, setConsultaError] = useState<string | null>(null);

	// --- Cruce de IMEIs state ---
	const [cruceImei1, setCruceImei1] = useState('');
	const [cruceImei2, setCruceImei2] = useState('');
	const [cruceLoading, setCruceLoading] = useState(false);
	const [cruceResult, setCruceResult] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

	// --- Precio máximo de venta state ---
	const [priceOverrideImei, setPriceOverrideImei] = useState('');
	const [priceOverrideCountry, setPriceOverrideCountry] = useState(internalCountry?.get() || userData?.country || 'venezuela');
	const [priceOverrideAmount, setPriceOverrideAmount] = useState('');
	const [priceOverrideLoading, setPriceOverrideLoading] = useState(false);
	const [priceOverrideResult, setPriceOverrideResult] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

	// --- Solicitud de bloqueo / desbloqueo state ---
	const [lockRequestImei, setLockRequestImei] = useState('');
	const [lockRequestAction, setLockRequestAction] = useState<ImeiLockAction>('');
	const [lockRequestLoading, setLockRequestLoading] = useState(false);
	const [lockRequestTarget, setLockRequestTarget] = useState<DeviceTarget | null>(null);
	const [lockRequestResult, setLockRequestResult] = useState<{ message: string; variant: 'success' | 'error' | 'warning' } | null>(null);

	const tabs = [
		{ id: 'consulta', label: 'Consulta de IMEI', icon: Search },
		{ id: 'bloqueo', label: 'Solicitar bloqueo/desbloqueo', icon: Lock },
		{ id: 'liberacion', label: 'Liberación de IMEI', icon: Unlock },
		{ id: 'cruce', label: 'Cruce de IMEIs', icon: ArrowRightLeft },
		{ id: 'precio', label: 'Cambiar precio de venta de equipo', icon: Tag },
	];

	// --- Handlers ---

	const handleRelease = async (e: React.FormEvent) => {
		e.preventDefault();
		const digits = releaseImei.replace(/\D/g, '');
		if (digits.length !== 15) {
			setReleaseResult({ message: 'El IMEI debe tener 15 dígitos', variant: 'warning' });
			return;
		}

		setReleaseLoading(true);
		setReleaseResult(null);

		const res = await unsafeAPI(
			'POST',
			'https://api.krece.app/administration/release_imei_from_provider/',
			{ imei: digits },
			userData,
			internalCountry.get(),
		);

		if (!res) {
			setReleaseResult({ message: 'Error de conexión', variant: 'error' });
		} else if (res.success) {
			setReleaseResult({ message: `IMEI liberado exitosamente del proveedor: ${res.provider}`, variant: 'success' });
			setReleaseImei('');
		} else {
			setReleaseResult({ message: res.status || 'Error al liberar el IMEI', variant: 'error' });
		}

		setReleaseLoading(false);
	};

	const handleConsulta = async (e: React.FormEvent) => {
		e.preventDefault();
		const digits = consultaImei.replace(/\D/g, '');
		if (digits.length !== 15) {
			setConsultaError('El IMEI debe tener 15 dígitos');
			return;
		}

		setConsultaLoading(true);
		setConsultaResult(null);
		setConsultaError(null);

		const res = await unsafeAPI(
			'POST',
			'https://api.krece.app/administration/consulta_imei/',
			{ imei: digits },
			userData,
			internalCountry.get(),
		);

		if (!res) {
			setConsultaError('Error de conexión');
		} else if (res.error) {
			setConsultaError(res.error);
		} else {
			setConsultaResult(res);
		}

		setConsultaLoading(false);
	};

	const handleCruce = async (e: React.FormEvent) => {
		e.preventDefault();
		const digits1 = cruceImei1.replace(/\D/g, '');
		const digits2 = cruceImei2.replace(/\D/g, '');

		if (digits1.length !== 15 || digits2.length !== 15) {
			setCruceResult({ message: 'Ambos IMEIs deben tener 15 dígitos', variant: 'error' });
			return;
		}
		if (digits1 === digits2) {
			setCruceResult({ message: 'Los IMEIs deben ser diferentes', variant: 'error' });
			return;
		}

		setCruceLoading(true);
		setCruceResult(null);

		const res = await unsafeAPI(
			'POST',
			'https://api.krece.app/administration/cruce_imeis/',
			{ imei_1: digits1, imei_2: digits2 },
			userData,
			internalCountry.get(),
		);

		if (!res) {
			setCruceResult({ message: 'Error de conexión', variant: 'error' });
		} else if (res.success) {
			setCruceResult({ message: res.message || 'IMEIs intercambiados exitosamente', variant: 'success' });
			setCruceImei1('');
			setCruceImei2('');
		} else {
			setCruceResult({ message: res.error || res.status || 'Error al intercambiar los IMEIs', variant: 'error' });
		}

		setCruceLoading(false);
	};

	const handleLockRequest = async (e: React.FormEvent) => {
		e.preventDefault();
		const imei = lockRequestImei.replace(/\D/g, '');

		if (imei.length !== 15) {
			setLockRequestResult({ message: 'El IMEI debe tener 15 dígitos', variant: 'warning' });
			setLockRequestTarget(null);
			return;
		}
		if (!lockRequestAction) {
			setLockRequestResult({ message: 'Seleccione si desea bloquear o desbloquear el equipo', variant: 'warning' });
			setLockRequestTarget(null);
			return;
		}

		setLockRequestLoading(true);
		setLockRequestResult(null);
		setLockRequestTarget(null);

		try {
			if (lockRequestAction === 'lock') {
				const target = await findActiveDeviceByImei(imei);
				setLockRequestTarget(target);

				if (!target) {
					setLockRequestResult({ message: 'No se encontró un equipo activo en Krece para este IMEI', variant: 'error' });
					return;
				}

				const [res, error] = await callAPI(
					'https://api.krece.app/lock_device/',
					'POST',
					{ device_id: target.id },
					userData,
					internalCountry.get(),
				);

				if (error || !res?.success) {
					setLockRequestResult({ message: error || res?.status || 'Error al solicitar el bloqueo del equipo', variant: 'error' });
				} else {
					setLockRequestResult({ message: res.status || `Solicitud de bloqueo enviada para el IMEI ${imei}`, variant: 'success' });
					setLockRequestImei('');
				}
				return;
			}

			const [res, error] = await callAPI(
				'https://api.krece.app/unlock_phone/',
				'POST',
				{ device_imei_no: imei },
				userData,
				internalCountry.get(),
			);

			if (error || !res?.success) {
				setLockRequestResult({ message: error || res?.status || 'Error al solicitar el desbloqueo del equipo', variant: 'error' });
			} else {
				setLockRequestResult({ message: res.status || `Solicitud de desbloqueo enviada para el IMEI ${imei}`, variant: 'success' });
				setLockRequestImei('');
			}
		} catch {
			setLockRequestResult({ message: 'Error al buscar el IMEI. Intente más tarde.', variant: 'error' });
		} finally {
			setLockRequestLoading(false);
		}
	};

	const handlePriceOverride = async (e: React.FormEvent) => {
		e.preventDefault();
		const imei = priceOverrideImei.replace(/\D/g, '');
		const amount = Number(priceOverrideAmount);

		if (imei.length !== 15) {
			setPriceOverrideResult({ message: 'El IMEI debe tener 15 dígitos', variant: 'error' });
			return;
		}
		if (!priceOverrideCountry) {
			setPriceOverrideResult({ message: 'Seleccione el país', variant: 'error' });
			return;
		}
		if (!Number.isFinite(amount) || amount <= 0) {
			setPriceOverrideResult({ message: 'Ingrese un precio máximo mayor a 0', variant: 'error' });
			return;
		}

		setPriceOverrideLoading(true);
		setPriceOverrideResult(null);

		const [res, error] = await callAPI(
			'https://api.krece.app/administration/price_control_override/',
			'POST',
			{
				country: priceOverrideCountry,
				imei,
				amount,
			},
			userData,
			priceOverrideCountry,
		);

		if (error || !res?.id) {
			setPriceOverrideResult({ message: error || 'Error al guardar el precio máximo', variant: 'error' });
		} else {
			setPriceOverrideResult({
				message: `El IMEI ${imei} podrá venderse hasta $${amount.toFixed(2)} en ${COUNTRY_LABELS[priceOverrideCountry as typeof COUNTRIES[number]]}`,
				variant: 'success',
			});
		}

		setPriceOverrideLoading(false);
	};

	return (
		<div className="mx-auto w-full max-w-3xl space-y-6 p-4 md:p-[1.25rem]">
			{/* Header */}
			<div className="flex items-center gap-4">
				<div className="rounded-lg bg-primary-500/10 p-3">
					<Smartphone className="size-8 text-primary-500" />
				</div>
				<div>
					<h2 className="text-2xl font-semibold text-neutral-100">Gestión de IMEIs</h2>
					<p className="text-sm text-neutral-400">Consulta, bloqueo, desbloqueo, liberación e intercambio de IMEIs</p>
				</div>
			</div>

			{/* Tab Bar */}
			<TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} hideLabelsOnMobile />

			{/* Tab: Solicitud de bloqueo / desbloqueo */}
			{activeTab === 'bloqueo' && (
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
					<p className="mb-4 text-sm text-neutral-400">
						Solicita el bloqueo o desbloqueo de un equipo activo por IMEI.
					</p>
					<form className="space-y-4" onSubmit={handleLockRequest}>
						<Input
							floatingLabel="IMEI (15 dígitos)"
							placeholder="358288123456789"
							value={lockRequestImei}
							onChange={setLockRequestImei}
							allowedChars="numbers_only"
							maxLength={15}
							clearable={false}
						/>

						<div className="space-y-1">
							<label className="text-sm font-medium tracking-tight text-neutral-300">Acción</label>
							<select
								value={lockRequestAction}
								onChange={(event) => setLockRequestAction(event.target.value as ImeiLockAction)}
								className="h-12 w-full rounded-lg border border-neutral-800 bg-neutral-925 px-3 text-sm text-neutral-100 transition-all focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 light:bg-neutral-950"
							>
								<option value="">Seleccione una acción</option>
								<option value="lock">Bloquear equipo</option>
								<option value="unlock">Desbloquear equipo</option>
							</select>
						</div>

						{lockRequestTarget && (
							<div className="rounded-lg border border-neutral-700/70 bg-neutral-900/40 p-4">
								<h3 className="mb-3 text-sm font-semibold text-neutral-200">Equipo encontrado</h3>
								<div className="grid gap-3 md:grid-cols-2">
									<div>
										<p className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">ID del equipo</p>
										<p className="break-all text-sm font-medium text-neutral-100">{lockRequestTarget.id}</p>
									</div>
									<div>
										<p className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Usuario</p>
										<p className="break-all text-sm font-medium text-neutral-100">{lockRequestTarget.userEmail || 'N/A'}</p>
									</div>
									<div>
										<p className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Tienda</p>
										<p className="text-sm font-medium text-neutral-100">{lockRequestTarget.store || 'N/A'}</p>
									</div>
									<div>
										<p className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Modelo</p>
										<p className="text-sm font-medium text-neutral-100">{lockRequestTarget.model || 'N/A'}</p>
									</div>
								</div>
							</div>
						)}

						{lockRequestResult && (
							<Notice
								message={lockRequestResult.message}
								variant={lockRequestResult.variant === 'warning' ? 'error' : lockRequestResult.variant}
							/>
						)}

						<Button type="submit" variant="primary" className="!w-fit" loading={lockRequestLoading}>
							{lockRequestAction === 'unlock' ? 'Solicitar desbloqueo' : 'Solicitar bloqueo'}
						</Button>
					</form>
				</div>
			)}

			{/* Tab: Liberación de IMEI */}
			{activeTab === 'liberacion' && (
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
					<p className="mb-4 text-sm text-neutral-400">
						Busca el IMEI en todos los proveedores MDM y lo libera del proveedor encontrado
					</p>
					<form className="space-y-4" onSubmit={handleRelease}>
						<Input
							floatingLabel="IMEI (15 dígitos)"
							placeholder="358288123456789"
							value={releaseImei}
							onChange={setReleaseImei}
							allowedChars="numbers_only"
							maxLength={15}
							clearable={false}
						/>

						{releaseResult && (
							<Notice
								message={releaseResult.message}
								variant={releaseResult.variant === 'warning' ? 'error' : releaseResult.variant}
							/>
						)}

						<Button type="submit" variant="primary" className="!w-fit" loading={releaseLoading}>
							Liberar IMEI
						</Button>
					</form>
				</div>
			)}

			{/* Tab: Consulta de IMEI */}
			{activeTab === 'consulta' && (
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
					<p className="mb-4 text-sm text-neutral-400">
						Consulta el estado de un IMEI en el sistema
					</p>
					<form className="space-y-4" onSubmit={handleConsulta}>
						<Input
							floatingLabel="IMEI (15 dígitos)"
							placeholder="358288123456789"
							value={consultaImei}
							onChange={setConsultaImei}
							allowedChars="numbers_only"
							maxLength={15}
							clearable={false}
						/>

						{consultaError && <Notice message={consultaError} variant="error" />}

						<Button type="submit" variant="primary" className="!w-fit" loading={consultaLoading}>
							Consultar IMEI
						</Button>
					</form>

					{consultaResult && (
						<div className="mt-6 rounded-lg border border-neutral-700 bg-neutral-800/50 p-4">
							<div className="mb-4 flex items-center justify-between gap-3">
								<h3 className="text-sm font-semibold text-neutral-200">Resultado de la consulta</h3>
								{typeof consultaResult.success === 'boolean' && (
									<span
										className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${
											consultaResult.success ? toneClasses.success : toneClasses.error
										}`}
									>
										{consultaResult.success ? 'Consulta exitosa' : 'Consulta fallida'}
									</span>
								)}
							</div>

							{typeof consultaResult.status === 'string' ? (
								<div className="space-y-3">
									{parseConsultaStatus(consultaResult.status).map((section) => (
										<div key={section.title} className="rounded-lg border border-neutral-700/70 bg-neutral-900/40 p-4">
											<div className="mb-3 flex items-center justify-between gap-3">
												<h4 className="text-sm font-semibold text-primary-200">{section.title}</h4>
												{section.chips.length > 0 && (
													<span className="text-xs text-neutral-500">
														{section.chips.length === 1 ? '1 proveedor activo' : `${section.chips.length} proveedores activos`}
													</span>
												)}
											</div>

											{section.chips.length > 0 && (
												<div className="flex flex-wrap gap-2">
													{section.chips.map((chip) => (
														<span
															key={chip}
															className="rounded-full bg-primary-500/10 px-3 py-1 text-xs font-semibold text-primary-200 ring-1 ring-primary-500/30"
														>
															{chip}
														</span>
													))}
												</div>
											)}

											{section.items.length > 0 && (
												<div className="grid gap-3 md:grid-cols-2">
													{section.items.map((item) => (
														<div key={`${section.title}-${item.label}`} className="rounded-md bg-neutral-950/40 p-3">
															<p className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">{item.label}</p>
															<span
																className={`inline-flex max-w-full break-all rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${toneClasses[item.tone]}`}
															>
																{item.value}
															</span>
														</div>
													))}
												</div>
											)}
										</div>
									))}
								</div>
							) : (
								<div className="space-y-2">
									{Object.entries(consultaResult).map(([key, value]) => (
										<div key={key} className="flex items-center justify-between border-b border-neutral-700/50 py-2 last:border-0">
											<span className="text-sm capitalize text-neutral-400">{key.replace(/_/g, ' ')}</span>
											<span className="text-sm font-medium text-neutral-100">{String(value)}</span>
										</div>
									))}
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{/* Tab: Cruce de IMEIs */}
			{activeTab === 'cruce' && (
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
					<p className="mb-4 text-sm text-neutral-400">
						Intercambia los IMEIs entre dos usuarios. El IMEI del usuario A pasará al usuario B y viceversa.
					</p>
					<form className="space-y-4" onSubmit={handleCruce}>
						<Input
							floatingLabel="IMEI 1 (15 dígitos)"
							placeholder="358288123456789"
							value={cruceImei1}
							onChange={setCruceImei1}
							allowedChars="numbers_only"
							maxLength={15}
							clearable={false}
						/>
						<Input
							floatingLabel="IMEI 2 (15 dígitos)"
							placeholder="358288987654321"
							value={cruceImei2}
							onChange={setCruceImei2}
							allowedChars="numbers_only"
							maxLength={15}
							clearable={false}
						/>

						{cruceResult && <Notice message={cruceResult.message} variant={cruceResult.variant} />}

						<Button type="submit" variant="primary" className="!w-fit" loading={cruceLoading}>
							Intercambiar IMEIs
						</Button>
					</form>
				</div>
			)}

			{/* Tab: Cambiar precio de venta de equipo */}
			{activeTab === 'precio' && (
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
					<p className="mb-4 text-sm text-neutral-400">
						Configura el precio máximo permitido para que un IMEI específico pueda venderse en un país.
					</p>
					<form className="space-y-4" onSubmit={handlePriceOverride}>
						<Input
							floatingLabel="IMEI (15 dígitos)"
							placeholder="358288123456789"
							value={priceOverrideImei}
							onChange={setPriceOverrideImei}
							allowedChars="numbers_only"
							maxLength={15}
							clearable={false}
						/>

						<div className="space-y-1">
							<label className="text-sm font-medium tracking-tight text-neutral-300">País</label>
							<select
								value={priceOverrideCountry}
								onChange={(event) => setPriceOverrideCountry(event.target.value)}
								className="h-12 w-full rounded-lg border border-neutral-800 bg-neutral-925 px-3 text-sm text-neutral-100 transition-all focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 light:bg-neutral-950"
							>
								{COUNTRIES.map((country) => (
									<option key={country} value={country}>
										{COUNTRY_LABELS[country]}
									</option>
								))}
							</select>
						</div>

						<Input
							floatingLabel="Precio máximo"
							placeholder="240"
							value={priceOverrideAmount}
							onChange={setPriceOverrideAmount}
							allowedChars="numeric"
							prefix="$"
							clearable={false}
						/>

						{priceOverrideResult && <Notice message={priceOverrideResult.message} variant={priceOverrideResult.variant} />}

						<Button type="submit" variant="primary" className="!w-fit" loading={priceOverrideLoading}>
							Guardar precio máximo
						</Button>
					</form>
				</div>
			)}
		</div>
	);
}
